/**
 * Document Preview Modal
 *
 * Full-screen Radix Dialog shell around the shared DocumentViewer. Used for
 * every citation preview that has no docked reader to hand off to, and for
 * docked-capable previews on narrow screens.
 *
 * On wide screens, chat previews (context 'chat') and Analysis-tab previews
 * (context 'analysis') dock as a side panel instead — see useDockedPreview /
 * useDockedAnalysisPreview / ChatDocumentPanel — and this modal self-hides.
 * That only happens on a page that registered a dock host (useDockHost):
 * elsewhere, e.g. the section editor's AI panel, the modal opens at any width.
 * Library and Strategy previews always use the modal.
 */

import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { DocumentViewer } from '@/components/ui/document-viewer'
import { usePreviewStore } from '@/stores/preview-store'
import { useDockedPreview, useDockedAnalysisPreview } from '@/hooks/useDockedPreview'

export function DocumentPreviewModal() {
  const isOpen = usePreviewStore((s) => s.isOpen)
  const documentName = usePreviewStore((s) => s.documentName)
  const closePreview = usePreviewStore((s) => s.closePreview)
  // The two dock hooks are mutually exclusive (they test different contexts),
  // so at most one panel ever claims the preview.
  const dockedInChat = useDockedPreview()
  const dockedInAnalysis = useDockedAnalysisPreview()
  const shouldDock = dockedInChat || dockedInAnalysis

  return (
    <Dialog open={isOpen && !shouldDock} onOpenChange={(open) => !open && closePreview()}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] max-h-[95vh] p-0 gap-0 flex flex-col [&>button.absolute]:hidden">
        {/* Visually-hidden title/description for Radix a11y — the visible
            header lives inside DocumentViewer (a plain heading). */}
        <DialogTitle className="sr-only">{documentName ?? 'Document'}</DialogTitle>
        <DialogDescription className="sr-only">Document preview</DialogDescription>
        <DocumentViewer onClose={closePreview} />
      </DialogContent>
    </Dialog>
  )
}
